import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(15,15,20,0.78)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, overflowY: 'auto',
};
const card = {
    background: 'white', borderRadius: 16, padding: 28, width: '100%', maxWidth: 520,
    boxShadow: '0 24px 60px rgba(0,0,0,0.4)', fontFamily: "'Segoe UI', Arial, sans-serif", color: '#1A1A1A',
    maxHeight: '92vh', overflowY: 'auto',
};
const inputStyle = {
    width: '100%', padding: '11px 13px', borderRadius: 10, border: '1.5px solid #e2e8f0',
    fontSize: 14, boxSizing: 'border-box', outline: 'none',
};
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, margin: '14px 0 6px' };
const primaryBtn = {
    padding: '12px 18px', background: '#1A1A1A', color: 'white', border: 'none', borderRadius: 10,
    fontWeight: 700, fontSize: 14, cursor: 'pointer',
};
const ghostBtn = {
    padding: '12px 18px', background: '#f1f5f9', color: '#1A1A1A', border: 'none', borderRadius: 10,
    fontWeight: 600, fontSize: 14, cursor: 'pointer', textDecoration: 'none', display: 'inline-block',
};
const muted = { fontSize: 13, color: '#64748b', lineHeight: 1.55 };

const ERROR_KEYS = {
    nickname_invalid: 'errNickname',
    undertaking_required: 'errUndertaking',
    personal_data_detected: 'errPersonal',
    too_many_requests: 'errTooMany',
    queue_full: 'errQueueFull',
};

/**
 * Окно «Регистрация закрыта (нет уведомления РКН)»: сообщение, заявка тестировщика с обязательством
 * и показ статуса. Показывается поверх формы, пока регистрация закрыта и тестовый доступ не одобрен.
 */
export default function RegistrationGateModal({ gate }) {
    const { t } = useTranslation(['registrationAccess']);
    const [isTester, setIsTester] = useState(false);
    const [nickname, setNickname] = useState('');
    const [comment, setComment] = useState('');
    const [accepted, setAccepted] = useState(false);
    const [sending, setSending] = useState(false);
    const [checking, setChecking] = useState(false);
    const [error, setError] = useState('');
    const [createdCode, setCreatedCode] = useState('');
    const [copied, setCopied] = useState(false);
    const [showCodeInput, setShowCodeInput] = useState(false);
    const [codeInput, setCodeInput] = useState('');
    const [startNew, setStartNew] = useState(false);

    if (!gate.locked) return null;

    const rejected = ['Rejected', 'Revoked'].includes(gate.status) && !startNew;
    const pending = gate.status === 'Pending' && !!gate.code;

    async function submit(e) {
        e.preventDefault();
        setError('');
        if (nickname.trim().length < 2) { setError(t('registrationAccess:gate.errNickname')); return; }
        if (!accepted) { setError(t('registrationAccess:gate.errUndertaking')); return; }
        setSending(true);
        const res = await gate.submitApplication({ nickname: nickname.trim(), comment: comment.trim() });
        setSending(false);
        if (res.ok) {
            setCreatedCode(res.code);
            setStartNew(false);
        } else {
            setError(t(`registrationAccess:gate.${ERROR_KEYS[res.error] || 'errGeneric'}`));
        }
    }

    async function checkStatus() {
        setChecking(true);
        await gate.refresh();
        setChecking(false);
    }

    async function applyCode() {
        if (!codeInput.trim()) return;
        setChecking(true);
        setError('');
        await gate.applyCode(codeInput);
        setChecking(false);
    }

    async function copyCode() {
        try {
            await navigator.clipboard.writeText(createdCode);
            setCopied(true);
        } catch { /* без буфера обмена код можно выделить вручную */ }
    }

    const homeLink = <a href="/" style={ghostBtn}>{t('registrationAccess:gate.home')}</a>;

    let content;
    if (createdCode) {
        content = (
            <>
                <h2 style={{ margin: '0 0 10px', fontSize: 20 }}>{t('registrationAccess:gate.createdTitle')}</h2>
                <p style={muted}>{t('registrationAccess:gate.createdBody')}</p>
                <div style={{
                    margin: '14px 0', padding: '14px 16px', background: '#f8fafc', border: '1.5px dashed #94a3b8',
                    borderRadius: 10, fontFamily: 'ui-monospace, Consolas, monospace', fontSize: 20,
                    fontWeight: 700, letterSpacing: 1, textAlign: 'center', userSelect: 'all',
                }}>
                    {createdCode}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button type="button" style={primaryBtn} onClick={copyCode}>
                        {copied ? t('registrationAccess:gate.copied') : t('registrationAccess:gate.copy')}
                    </button>
                    <button type="button" style={ghostBtn} onClick={() => setCreatedCode('')}>
                        {t('registrationAccess:gate.checkStatus')}
                    </button>
                </div>
            </>
        );
    } else if (pending) {
        content = (
            <>
                <h2 style={{ margin: '0 0 10px', fontSize: 20 }}>{t('registrationAccess:gate.pendingTitle')}</h2>
                <p style={muted}>{t('registrationAccess:gate.pendingBody')}</p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
                    <button type="button" style={primaryBtn} disabled={checking} onClick={checkStatus}>
                        {checking ? t('registrationAccess:gate.checking') : t('registrationAccess:gate.checkStatus')}
                    </button>
                    {homeLink}
                </div>
            </>
        );
    } else if (rejected) {
        content = (
            <>
                <h2 style={{ margin: '0 0 10px', fontSize: 20 }}>{t('registrationAccess:gate.rejectedTitle')}</h2>
                <p style={muted}>{t('registrationAccess:gate.rejectedBody')}</p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
                    {homeLink}
                    <button type="button" style={ghostBtn} onClick={() => { gate.forgetCode(); setStartNew(true); }}>
                        {t('registrationAccess:gate.newApplication')}
                    </button>
                </div>
            </>
        );
    } else {
        content = (
            <>
                <h2 style={{ margin: '0 0 10px', fontSize: 20 }}>{t('registrationAccess:gate.title')}</h2>
                <p style={{ ...muted, color: '#334155' }}>{t('registrationAccess:gate.body')}</p>
                <p style={muted}>{t('registrationAccess:gate.testerIntro')}</p>

                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 14, margin: '14px 0', cursor: 'pointer' }}>
                    <input type="checkbox" checked={isTester} onChange={e => setIsTester(e.target.checked)} />
                    {t('registrationAccess:gate.imTester')}
                </label>

                {isTester && (
                    <form onSubmit={submit}>
                        <label style={labelStyle}>{t('registrationAccess:gate.nickname')}</label>
                        <input
                            style={inputStyle} value={nickname} maxLength={64} autoComplete="off"
                            onChange={e => setNickname(e.target.value)}
                        />
                        <p style={{ ...muted, margin: '4px 0 0' }}>{t('registrationAccess:gate.nicknameHint')}</p>

                        <label style={labelStyle}>{t('registrationAccess:gate.comment')}</label>
                        <textarea
                            style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }} value={comment} maxLength={500}
                            placeholder={t('registrationAccess:gate.commentPlaceholder')}
                            onChange={e => setComment(e.target.value)}
                        />

                        <div style={{ marginTop: 16, padding: 14, background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 10 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
                                {t('registrationAccess:gate.undertakingTitle')}
                            </div>
                            <p style={{ ...muted, color: '#7c2d12', margin: '0 0 6px' }}>
                                {t('registrationAccess:gate.undertakingIntro')}
                            </p>
                            <ol style={{ ...muted, color: '#7c2d12', margin: '0 0 8px', paddingLeft: 20 }}>
                                {['u1', 'u2', 'u3', 'u4', 'u5'].map(k => (
                                    <li key={k} style={{ marginBottom: 4 }}>{t(`registrationAccess:gate.${k}`)}</li>
                                ))}
                            </ol>
                            <p style={{ ...muted, fontSize: 12, margin: 0 }}>{t('registrationAccess:gate.undertakingNote')}</p>
                        </div>

                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, margin: '12px 0', cursor: 'pointer' }}>
                            <input type="checkbox" style={{ marginTop: 3 }} checked={accepted} onChange={e => setAccepted(e.target.checked)} />
                            <span>{t('registrationAccess:gate.undertakingAccept')}</span>
                        </label>

                        {error && <div style={{ color: '#c62828', fontSize: 13, marginBottom: 10 }}>{error}</div>}

                        <button type="submit" style={{ ...primaryBtn, opacity: sending ? 0.7 : 1 }} disabled={sending}>
                            {sending ? t('registrationAccess:gate.sending') : t('registrationAccess:gate.submit')}
                        </button>
                    </form>
                )}

                <div style={{ marginTop: 18, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    {homeLink}
                    <button
                        type="button"
                        onClick={() => setShowCodeInput(v => !v)}
                        style={{ background: 'none', border: 'none', color: '#2563eb', fontSize: 13, cursor: 'pointer', padding: 0 }}
                    >
                        {t('registrationAccess:gate.haveCode')}
                    </button>
                </div>

                {showCodeInput && (
                    <div style={{ marginTop: 10 }}>
                        <label style={{ ...labelStyle, marginTop: 4 }}>{t('registrationAccess:gate.codeLabel')}</label>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <input
                                style={{ ...inputStyle, fontFamily: 'ui-monospace, Consolas, monospace' }}
                                value={codeInput} placeholder="TST-XXXX-XXXX-XXXX" autoComplete="off"
                                onChange={e => setCodeInput(e.target.value)}
                            />
                            <button type="button" style={primaryBtn} disabled={checking} onClick={applyCode}>
                                {t('registrationAccess:gate.codeApply')}
                            </button>
                        </div>
                        {gate.status === 'NotFound' && (
                            <div style={{ color: '#c62828', fontSize: 13, marginTop: 6 }}>
                                {t('registrationAccess:gate.codeNotFound')}
                            </div>
                        )}
                    </div>
                )}
            </>
        );
    }

    return (
        <div style={overlay} role="dialog" aria-modal="true">
            <div style={card}>{content}</div>
        </div>
    );
}

/** Плашка над формой для допущенного тестировщика. */
export function TesterModeBanner({ left }) {
    const { t } = useTranslation(['registrationAccess']);
    return (
        <div style={{
            background: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74', borderRadius: 10,
            padding: '12px 14px', fontSize: 13, lineHeight: 1.5, marginBottom: 16,
        }}>
            <strong>{t('registrationAccess:banner.title')}.</strong> {t('registrationAccess:banner.body')}{' '}
            {t('registrationAccess:banner.left', { count: left })}
        </div>
    );
}
